import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * When the same account logs in on another device, this session gets a 10s
 * warning then is force-logged-out.
 */
export default function SessionReplaceWarning() {
  const { isAuthenticated, logout } = useAuth();
  const [warning, setWarning] = useState(null);
  const forcedRef = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!isAuthenticated) {
      setWarning(null);
      forcedRef.current = false;
      return undefined;
    }

    let cancelled = false;

    const forceLeave = async (message) => {
      if (forcedRef.current) return;
      forcedRef.current = true;
      setWarning({
        seconds_remaining: 0,
        message: message || 'Oturumunuz sonlandırıldı. Başka bir cihazdan giriş yapıldı.',
      });
      try {
        await logoutRef.current({ redirectTo: '/login?reason=session_replaced' });
      } catch {
        window.location.href = '/login?reason=session_replaced';
      }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/auth/session-status`, {
          withCredentials: true,
        });
        if (cancelled) return;
        const data = res.data || {};

        if (data.forced_logout || data.ok === false) {
          await forceLeave(data.message);
          return;
        }

        if (data.replace_pending) {
          setWarning({
            seconds_remaining: data.seconds_remaining ?? 0,
            message:
              data.message ||
              'Başka bir cihazdan giriş yapıldı. Oturumunuz sonlanacaktır.',
          });
          if ((data.seconds_remaining ?? 0) <= 0) {
            await forceLeave(data.message);
          }
        } else {
          setWarning(null);
        }
      } catch {
        // Network blip — keep previous warning state
      }
    };

    poll();
    const id = setInterval(poll, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAuthenticated]);

  if (!warning) return null;

  const seconds = Math.max(0, Number(warning.seconds_remaining) || 0);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-replace-title"
      data-testid="session-replace-warning"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-amber-200 overflow-hidden">
        <div className="bg-amber-50 px-5 py-4 flex items-start gap-3 border-b border-amber-100">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
          <div>
            <h2 id="session-replace-title" className="text-base font-semibold text-gray-900">
              Oturumunuz sonlanacaktır
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {warning.message}
            </p>
          </div>
        </div>
        <div className="px-5 py-6 text-center">
          <div
            className="text-4xl font-bold tabular-nums text-amber-700"
            data-testid="session-replace-countdown"
          >
            {seconds}
          </div>
          <p className="text-xs text-gray-500 mt-2">saniye içinde bu oturum kapatılacak</p>
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={() => logoutRef.current({ redirectTo: '/login?reason=session_replaced' })}
            className="w-full px-4 py-2.5 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800"
            data-testid="session-replace-logout-now"
          >
            Hemen çıkış yap
          </button>
        </div>
      </div>
    </div>
  );
}
